import * as React from "react";
import { useState } from "react";

import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import dayjs, { Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { List as LinqList } from "linqts";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { makeStyles } from "tss-react/mui";

import styles from "./Calendar.module.scss";
import CalendarNavigation from "./CalendarNavigator";
import { RootState } from "../../../store/store";
import { SelectedRollDate } from "../../../types/models";
import { ISessionAvailability } from "../../../types/types";
import dateOnlyToJson from "../../../utils/dateOnlyToJson";
import { getCareTypeColor, getNextBusinessDay, getMonthViewDates, getStyleColor } from "../../../utils/helpers";

dayjs.extend(dateOnlyToJson);
dayjs.extend(isoWeek);

const useStyles = makeStyles()((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "white",
  },
  popover: {
    pointerEvents: "auto",
  },

  button: {
    pointerEvents: "auto",
    color: "black",
    borderWidth: 1,
    borderColor: "black",
    fontWeight: "bold",
    textTransform: "none",
  },

  editCancelButton: {
    pointerEvents: "auto",
    color: "black",
    borderWidth: 1,
    borderColor: "black",
    fontWeight: "bold",
    textTransform: "none",
    width: 170,
    height: 40,
  },

  wrap: {
    flexBasis: "100%",
  },
  gridItem: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    maxWidth: `${100 / 5}%`,
  },
  legend: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  legendItem: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginRight: "10px",
    marginBottom: "10px",
  },
  legendItemLabel: {
    marginLeft: "10px",
  },
  calDay: {
    borderRadius: 18,
    minWidth: 40,
    backgroundColor: getStyleColor("--custom-color-mid-grey"),
    cursor: "not-allowed",
    pointerEvents: "none",
    "@media screen and (min-width: 1280px)": {
      minWidth: 44,
      borderRadius: 20,
    },
    "&:hover": {
      backgroundColor: getStyleColor("--custom-color-mid-grey"),
    },
  },

  calDayOn: {
    borderRadius: 16,
    minWidth: 32,
    "@media screen and (min-width: 1280px)": {
      minWidth: 44,
      borderRadius: 22,
    },
  },
  calDayToday: {
    backgroundColor: getStyleColor("--color-primary"),
    borderRadius: 32,
    borderWidth: 1,
    borderColor: "black",
    borderStyle: "solid",
    minWidth: 32,
    minHeight: 32,
    "@media screen and (min-width: 1280px)": {
      minWidth: 44,
      borderRadius: 44,
      minHeight: 44,
    },
    "&:hover": {
      backgroundColor: getStyleColor("--color-accent-1"),
    },
  },
  calDaySelected: {
    backgroundColor: getStyleColor("--color-accent-4"),
    borderRadius: 32,
    minWidth: 32,
    minHeight: 32,
    "@media screen and (min-width: 1280px)": {
      minWidth: 44,
      borderRadius: 44,
      minHeight: 44,
    },
    "&:hover": {
      color: "black",
      backgroundColor: getStyleColor("--custom-color-mid-grey"),
    },
  },
  badge: {
    zIndex: 1,
    position: "absolute",
    top: -12,
    width: 20,
    height: 20,
  },
  childName: {
    maxWidth: "100px",
    wordBreak: "break-all",
    overflowWrap: "anywhere",
  },
}));

interface ICasualCalendarProps {
  rollsCount: number;
  selectedRollDates: SelectedRollDate[];
  unAvailableDates: ISessionAvailability[];
  date: Dayjs;
  onDateChanged: (date: Dayjs) => void;
}

CalendarCasual.propTypes = {
  trigger: PropTypes.string,
};

export function CalendarCasual(props: ICasualCalendarProps) {
  const { date, rollsCount, selectedRollDates, unAvailableDates, onDateChanged } = props;
  const commonData = useSelector((state: RootState) => state.commonData);
  const { isLoading } = useSelector((state: RootState) => state.booking);

  const [selectedDate, setSelectedDate] = useState<Dayjs>(date);

  const { classes } = useStyles();

  const [anchorEl, setAnchorEl] = React.useState(null);
  const node = React.useRef<HTMLDivElement | null>(null);

  const DAYS_OF_THE_WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri"];

  const handleClick = (event: any) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  var daysToView = getMonthViewDates(date);

  var now = dayjs();
  var previousMonthDate = selectedDate.subtract(1, "month").startOf("month");
  let previousMonthMonday = getNextBusinessDay(previousMonthDate);
  if (previousMonthMonday < now) {
    previousMonthMonday = now;
  }

  var nextMonthDate = selectedDate.add(1, "month").startOf("month");
  let nextMonthMonday = getNextBusinessDay(nextMonthDate);

  let currentMonthSelected = date.isSame(now, "month");

  return (
    <Grid item xs={12}>
      <CalendarNavigation
        date={date}
        isWeekView={false}
        prevDisabled={currentMonthSelected}
        onBackClick={() => {
          setSelectedDate(previousMonthMonday);
          onDateChanged(previousMonthMonday);
        }}
        onForwardClick={() => {
          setSelectedDate(nextMonthMonday);
          onDateChanged(nextMonthMonday);
        }}
      />
      <Backdrop className={classes.backdrop} open={isLoading || commonData.isLoading}>
        <CircularProgress color="primary" />
      </Backdrop>

      <Grid container id="bookingCalendar">
        {DAYS_OF_THE_WEEK.map((d, index) => (
          <Grid item key={index} xs className={classes.gridItem}>
            <Grid container justifyContent="center" alignItems="center">
              <strong className={styles["DayOfTheWeek"]}>{d}</strong>
            </Grid>
          </Grid>
        ))}
        <Grid item xs={12}>
          <Divider />
        </Grid>
      </Grid>

      <Grid container justifyContent="flex-start">
        {daysToView.map((currentDay, index) => {
          const isToday = currentDay.isSame(dayjs(), "date");
          const isSameMonth = currentDay.isSame(date, "month");
          const isWeekend = currentDay.day() === 0 || currentDay.day() === 6;
          const isBeforeToday = currentDay.isBefore(dayjs(), "date");
          const isSelectedDate = currentDay.isSame(selectedDate, "date");
          const todaysBookings = selectedRollDates?.filter((b) =>
            dayjs(b.date ?? new Date()).isSame(currentDay, "date")
          );
          const byRoll = new LinqList<SelectedRollDate>(todaysBookings).GroupBy((t) =>
            t.roll?.rollClassificationId === 9 ? "PFD" : t.roll?.sessionType ?? ""
          );

          const unAvailableDatesOnDay = unAvailableDates.filter(
            (u) => currentDay.isSame(u.date, "date") && u.isAvailableForCasualBooking === false
          );
          const isUnAvailable = isBeforeToday || rollsCount === unAvailableDatesOnDay.length;

          if (isWeekend) return null;
          return (
            <React.Fragment key={index}>
              <Grid
                item
                xs={12 / 5}
                ref={(el: any) => {
                  node.current = el;
                }}
                className={classes.gridItem}
              >
                <Grid container justifyContent="center" alignItems="center">
                  {isSameMonth ? (
                    <Button
                      className={
                        isSelectedDate
                          ? classes.calDaySelected
                          : isToday
                          ? classes.calDayToday
                          : isUnAvailable
                          ? classes.calDay
                          : classes.calDayOn
                      }
                      onClick={(event: any) => {
                        if (!isUnAvailable) {
                          handleClick(event);
                          setSelectedDate(currentDay);
                          onDateChanged(currentDay);
                        }
                      }}
                    >
                      <Typography
                        className={
                          isSelectedDate
                            ? styles.Selected
                            : isToday
                            ? styles.Today
                            : isUnAvailable
                            ? styles.OtherDay
                            : styles.NormalDay
                        }
                      >
                        {currentDay.date()}
                      </Typography>
                    </Button>
                  ) : null}
                </Grid>
                {isSameMonth ? (
                  <Grid container justifyContent="center" alignItems="center">
                    {Object.keys(byRoll).length > 0
                      ? Object.keys(byRoll).map((roll, index) => {
                          return (
                            <React.Fragment key={index}>
                              {byRoll[roll].map((bk, index) => {
                                if (index === 0) {
                                  const careType =
                                    bk.roll?.rollClassificationId === 9 ? "PFD" : bk.roll?.sessionType ?? "";
                                  var careTypeColor = getCareTypeColor(careType);
                                  return (
                                    <span
                                      key={index}
                                      className={`${styles["TrianglePad"]} ${styles[careTypeColor]}`}
                                      title={careTypeColor}
                                      aria-label={careTypeColor}
                                    />
                                  );
                                }
                                return null;
                              })}
                            </React.Fragment>
                          );
                        })
                      : null}
                  </Grid>
                ) : null}
              </Grid>
              {(index + 1) % 7 === 0 && <Grid item className={classes.wrap} />}
            </React.Fragment>
          );
        })}
        <Box paddingTop={2} />
        <>
          <Grid container justifyContent="flex-start" alignItems="center">
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} className={classes.legend}>
              <Grid container justifyContent="flex-start" alignItems="center" spacing={2}>
                <Grid item>
                  <Grid container justifyContent="flex-start" alignItems="center">
                    <span className={styles["Bsc12"]} />
                    <span className={classes.legendItemLabel}>Before School Care</span>
                  </Grid>
                </Grid>
                <Grid item>
                  <Grid container justifyContent="flex-start" alignItems="center">
                    <span className={styles["Asc12"]} />
                    <span className={classes.legendItemLabel}>After School Care</span>
                  </Grid>
                </Grid>
                <Grid item>
                  <Grid container justifyContent="flex-start" alignItems="center">
                    <span className={styles["Vac12"]} />
                    <span className={classes.legendItemLabel}>Rocketeers</span>
                  </Grid>
                </Grid>
                <Grid item>
                  <Grid container justifyContent="flex-start" alignItems="center">
                    <span className={styles["Pfd12"]} />
                    <span className={classes.legendItemLabel}>Pupil Free Day</span>
                  </Grid>
                </Grid>
                <Grid item>
                  <Grid container justifyContent="flex-start" alignItems="center">
                    <span className={styles["Hl12"]} />
                    <span className={classes.legendItemLabel}>Public Holiday</span>
                  </Grid>
                </Grid>
                <Grid item>
                  <Grid container justifyContent="flex-start" alignItems="center">
                    <span className={styles["Ns12"]} />
                    <span className={classes.legendItemLabel}>No Sessions Available</span>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </>
      </Grid>
    </Grid>
  );
}
